import { ThemeProvider } from "@mui/material/styles";
import FullPageLoader from "./components/layout/FullPageLoader";
import {
  AuthProvider,
  withAuthenticationRequired,
} from "./hoc/withAuthenticationRequired";
import { withPlayerActiveRequired } from "./hoc/withPlayerActiveRequired";
import { withErrorBoundary } from "react-error-boundary";
import { Box, CssBaseline } from "@mui/material";
import { isMobile } from "react-device-detect";
import { Route, Routes, useMatch } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import theme from "./theme";
import Header from "./components/layout/Header";
import MobileHeader from "./components/layout/MobileHeader";
import { withScheduleProvider } from "./pages/schedule-page/context/schedule-context";
import { withHomeProvider } from "./pages/home-page/context/home-context";
import { useWebViewDetection } from "./hooks/useWebViewDetection";
import { AnalyticsObserver } from "~@/analytics/AnalyticsObserver";
import { trackAnalyticsEvent } from "~@/analytics/amplitude";
import { ANALYTICS_EVENTS } from "~@/analytics/events";

const HomePage = lazy(() => import("./pages/home-page"));
const SchedulePage = lazy(() => import("./pages/schedule-page"));
const PlayersPage = lazy(() => import("./pages/players-page"));
const PlayerPage = lazy(() => import("./pages/player-page"));
const TeamsPage = lazy(() => import("./pages/teams-page"));
const TeamPage = lazy(() => import("./pages/team-page"));
const StatisticsPage = lazy(() => import("./pages/statistics-page"));
const TournamentTablePage = lazy(() => import("./pages/tournament-table-page"));
const LeaderboardPage = lazy(() => import("./pages/leaderboard-page"));
const MatchPage = lazy(() => import("./pages/match-page"));
const TournamentPage = lazy(() => import("./pages/tournament-page"));
const AuthPage = lazy(() => import("./pages/auth-page"));
const ProfilePage = lazy(() => import("./pages/profile-page"));
const PersonalDataPage = lazy(() => import("./pages/profile-page/personal"));
const SectionAcademyPage = lazy(() => import("./pages/section-academy-page"));

const ProtectedHomePage = withAuthenticationRequired(HomePage);
const ProtectedProfilePage = withAuthenticationRequired(ProfilePage);
const ProtectedPersonalDataPage = withAuthenticationRequired(PersonalDataPage);

const FullyProtectedSchedulePage = withAuthenticationRequired(
  withPlayerActiveRequired(SchedulePage)
);
const FullyProtectedPlayersPage = withAuthenticationRequired(
  withPlayerActiveRequired(PlayersPage)
);
const FullyProtectedPlayerPage = withAuthenticationRequired(
  withPlayerActiveRequired(PlayerPage)
);
const FullyProtectedTeamsPage = withAuthenticationRequired(
  withPlayerActiveRequired(TeamsPage)
);
const FullyProtectedTeamPage = withAuthenticationRequired(
  withPlayerActiveRequired(TeamPage)
);
const FullyProtectedStatisticsPage = withAuthenticationRequired(
  withPlayerActiveRequired(StatisticsPage)
);
const FullyProtectedTournamentTablePage = withAuthenticationRequired(
  withPlayerActiveRequired(TournamentTablePage)
);
const FullyProtectedLeaderboardPage = withAuthenticationRequired(
  withPlayerActiveRequired(LeaderboardPage)
);
const FullyProtectedMatchPage = withAuthenticationRequired(
  withPlayerActiveRequired(MatchPage)
);
const FullyProtectedTournamentPage = withAuthenticationRequired(
  withPlayerActiveRequired(TournamentPage)
);

const ErrorFallback = () => {
  useEffect(() => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.CRASH, { source: "AppBoundary" });
  }, []);

  return <>Error...</>;
};

const App = () => {
  const match1 = useMatch("/players/:id");
  const match2 = useMatch("/teams/:id");
  const match3 = useMatch("/statistics");
  const match4 = useMatch("/matches/:id");
  const match5 = useMatch("/tournaments/:id");
  const match6 = useMatch("/tournament-table");
  const match7 = useMatch("/leaderboard");

  const { isWebView, isGoogleApp } = useWebViewDetection();

  return (
    <AuthProvider>
      <AnalyticsObserver>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              minHeight: "100vh",
              height: "100%",
              bgcolor: isMobile ? "background.paper" : "#f4f5f5",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                width: "100%",
                maxWidth: isMobile ? "100%" : "393px",
                bgcolor: "#f4f5f5",
                border: isMobile ? "none" : "1px solid #e5e5e5",
                position: isWebView ? "static" : "relative",
                overflow: "hidden",
              }}
            >
              {!match1 && !match2 && !match3 && !match4 && !match5 && !match6 && !match7 && (
                <>{isGoogleApp ? <Header /> : isWebView ? <MobileHeader /> : <Header />}</>
              )}
              <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <Suspense fallback={<FullPageLoader />}>
                  <Routes>
                  
                  <Route index element={<ProtectedHomePage />} />
                  <Route path="section-academy" element={<SectionAcademyPage />} />
                  
                  <Route
                    path="schedule"
                    element={<FullyProtectedSchedulePage />}
                  />
                  <Route path="players" element={<FullyProtectedPlayersPage />} />
                  <Route
                    path="players/:id"
                    element={<FullyProtectedPlayerPage />}
                  />
                  <Route path="teams" element={<FullyProtectedTeamsPage />} />
                  <Route path="teams/:id" element={<FullyProtectedTeamPage />} />
                  <Route
                    path="statistics"
                    element={<FullyProtectedStatisticsPage />}
                  />
                  <Route
                    path="tournament-table"
                    element={<FullyProtectedTournamentTablePage />}
                  />
                  <Route
                    path="leaderboard"
                    element={<FullyProtectedLeaderboardPage />}
                  />
                  <Route
                    path="matches/:id"
                    element={<FullyProtectedMatchPage />}
                  />
                  <Route
                    path="tournaments/:id"
                    element={<FullyProtectedTournamentPage />}
                  />

                  <Route path="login" element={<AuthPage />} />
                  <Route path="profile" element={<ProtectedProfilePage />} />
                  <Route path="profile/personal" element={<ProtectedPersonalDataPage />} />
                  </Routes>
                </Suspense>
              </Box>
            </Box>
          </Box>
        </ThemeProvider>
      </AnalyticsObserver>
    </AuthProvider>
  );
};

export default withErrorBoundary(withHomeProvider(withScheduleProvider(App)), {
  FallbackComponent: ErrorFallback,
});
